import i18n from '@/i18n'

function locale() {
  return i18n.language || 'en'
}

export function formatDate(value: string | Date, options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' }) {
  return new Intl.DateTimeFormat(locale(), options).format(typeof value === 'string' ? new Date(value) : value)
}

export function formatDateTime(value: string | Date) {
  return formatDate(value, { dateStyle: 'medium', timeStyle: 'short' })
}

export function formatNumber(value: number, options?: Intl.NumberFormatOptions) {
  return new Intl.NumberFormat(locale(), options).format(value)
}

export function countryName(code: string): string {
  try {
    return new Intl.DisplayNames([locale()], { type: 'region' }).of(code) ?? code
  } catch {
    return code
  }
}

export function currencyName(code: string, language: string = locale()): string {
  try {
    return new Intl.DisplayNames([language], { type: 'currency' }).of(code) ?? code
  } catch {
    return code
  }
}

const REGION_CODES = (() => {
  const codes: string[] = []
  for (let a = 65; a <= 90; a++) {
    for (let b = 65; b <= 90; b++) codes.push(String.fromCharCode(a, b))
  }
  return codes
})()

/** ISO 3166-1 alpha-2 codes that the runtime can name, sorted by localized name. */
export function listCountries(language: string = locale()): { code: string; name: string }[] {
  let names: Intl.DisplayNames
  try {
    names = new Intl.DisplayNames([language], { type: 'region', fallback: 'none' })
  } catch {
    return []
  }
  const out: { code: string; name: string }[] = []
  for (const code of REGION_CODES) {
    const name = names.of(code)
    // Skip unassigned codes and non-country regions (EU, UN, ZZ, XA...).
    if (name && name !== code && !['EU', 'UN', 'EZ', 'XA', 'XB', 'ZZ', 'QO', 'AC', 'CP', 'DG', 'EA', 'IC', 'TA', 'XK'].includes(code)) {
      out.push({ code, name })
    }
  }
  return out.sort((x, y) => x.name.localeCompare(y.name, language))
}

export function listCurrencies(language: string = locale()): { code: string; name: string }[] {
  const codes: string[] =
    typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('currency') : ['BRL', 'USD', 'EUR']
  return codes
    .map((code) => ({ code, name: currencyName(code, language) }))
    .sort((x, y) => x.code.localeCompare(y.code))
}

export function listTimeZones(): string[] {
  const zones = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : []
  return zones.includes('UTC') ? zones : ['UTC', ...zones]
}

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/** Default currency of a country, via its most likely locale. Falls back to undefined. */
const COUNTRY_CURRENCY: Record<string, string> = {
  BR: 'BRL', US: 'USD', PT: 'EUR', ES: 'EUR', FR: 'EUR', DE: 'EUR', IT: 'EUR', NL: 'EUR', IE: 'EUR', BE: 'EUR',
  AT: 'EUR', FI: 'EUR', GR: 'EUR', AR: 'ARS', CL: 'CLP', CO: 'COP', MX: 'MXN', PE: 'PEN', UY: 'UYU', PY: 'PYG',
  BO: 'BOB', EC: 'USD', VE: 'VES', GB: 'GBP', CA: 'CAD', AU: 'AUD', NZ: 'NZD', JP: 'JPY', CH: 'CHF', AO: 'AOA',
  MZ: 'MZN', CR: 'CRC', PA: 'PAB', DO: 'DOP', GT: 'GTQ', HN: 'HNL', NI: 'NIO', SV: 'USD', CU: 'CUP',
}

export function defaultCurrencyFor(country: string): string | undefined {
  return COUNTRY_CURRENCY[country]
}

/** `YYYY-MM-DD` shown in the user's local format, without any time zone shift. */
export function formatDateOnly(value: string, options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' }): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!m) return value
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return new Intl.DateTimeFormat(locale(), { ...options, timeZone: 'UTC' }).format(date)
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${formatNumber(bytes)} B`
  const units = ['KB', 'MB', 'GB']
  let value = bytes / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${formatNumber(value, { maximumFractionDigits: 1 })} ${units[unit]}`
}
